import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/integrations/supabase/AuthProvider';
import { Button } from "@/components/ui/button";
import { 
  Sparkles, Shield, Zap, Code, Layout, ArrowRight, CheckCircle2, 
  Bot, Blocks, Wand2, Terminal, Cpu, Globe, Database, 
  Layers, MousePointer2, GitBranch, Share2, MessageCircle
} from "lucide-react";
import logo from "@/assets/logo.png";

const LandingPage = () => {
  const { session } = useAuth();

  if (session) {
    return <Navigate to="/app" replace />;
  }

  const pricing = [
    {
      name: "Architect",
      price: "£0",
      desc: "For hobbyists building their first games.",
      features: ["£5 Weekly Refill", "3 Project Slots", "Standard Studio Bridge", "My Possessions Access"],
      button: "Start Building",
      accent: "border-white/10"
    },
    {
      name: "Architect Pro",
      price: "£6.99",
      desc: "The standard for professional creators.",
      features: ["£20 Weekly Refill", "Unlimited Projects", "Auto-Sync Heartbeat", "Vision System", "Custom Build Styles"],
      button: "Go Pro",
      highlight: true,
      accent: "border-sonas-accent/30 bg-sonas-accent/5"
    }
  ];

  const techStats = [
    { label: "Sync Latency", value: "< 200ms" },
    { label: "Logic Depth", value: "128k Tokens" },
    { label: "Bridge Uptime", value: "99.9%" },
    { label: "Luau Support", value: "Native" }
  ];

  return (
    <div className="min-h-screen bg-sonas-bg text-sonas-text font-sans selection:bg-sonas-accent/30">
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-sonas-accent/5 rounded-full blur-[120px] animate-pulse-slow" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-500/5 rounded-full blur-[120px] animate-pulse-slow" style={{ animationDelay: '2s' }} />
      </div>

      <nav className="fixed top-0 w-full z-50 glass-surface border-b border-white/5 h-16 flex items-center justify-between px-6 lg:px-12">
        <div className="flex items-center">
          <img src={logo} alt="Sonas" className="h-7 w-auto" />
        </div>
        <div className="flex items-center gap-8">
          <Link to="/login" className="text-xs font-bold uppercase tracking-widest text-sonas-dim hover:text-sonas-text transition-colors">Login</Link>
          <Link to="/login?signup=true">
            <Button className="bg-sonas-accent text-sonas-bg font-bold rounded-xl px-6 h-10 shadow-lg shadow-sonas-accent/20">Join the Forge</Button>
          </Link>
        </div>
      </nav>

      <main className="relative z-10 pt-40 pb-32">
        <section className="px-6 max-w-7xl mx-auto text-center space-y-12">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/[0.03] border border-white/10 rounded-full animate-in fade-in slide-in-from-bottom-4 duration-700">
            <span className="text-[10px] font-bold text-sonas-accent uppercase tracking-widest">Next 10 users get a £2 early-bird bonus.</span>
          </div>

          <h1 className="text-6xl lg:text-[100px] font-syne font-bold tracking-tight leading-[0.85] animate-in fade-in slide-in-from-bottom-8 duration-1000">
            Stop fighting the engine. <br /> <span className="text-sonas-accent">Just start building.</span>
          </h1>
          
          <p className="text-sonas-muted text-lg lg:text-2xl max-w-3xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-12 duration-1000">
            Sonas is your specialized co-pilot for Roblox. Plan complex systems, write optimized Luau, and sync everything directly to Studio in real-time.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-6">
            <Link to="/login">
              <Button className="h-16 px-12 rounded-2xl bg-sonas-accent text-sonas-bg font-bold text-xl shadow-2xl shadow-sonas-accent/20 gap-3 hover:scale-105 active:scale-95 transition-all">
                Enter the Forge <ArrowRight size={22} />
              </Button>
            </Link>
          </div>
        </section>

        <section className="mt-32 border-y border-white/5 bg-white/[0.01] backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {techStats.map((stat, i) => (
                <div key={i} className="text-center space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sonas-dim">{stat.label}</p>
                  <p className="text-3xl font-syne font-bold text-sonas-accent">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-56 px-6 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-10">
              <div className="space-y-4">
                <h2 className="text-4xl font-syne font-bold">Built for Creators, by Creators.</h2>
                <p className="text-sonas-muted leading-relaxed">We know Roblox Studio is powerful, but repetitive boilerplate can kill your momentum. Sonas understands your game's structure and helps you ship features in minutes, not hours.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { icon: Bot, label: "Context Aware", desc: "It knows your hierarchy and where scripts live." },
                  { icon: Blocks, label: "Clean Luau", desc: "Output that follows modern Studio standards." },
                  { icon: Shield, label: "Encrypted Bridge", desc: "Securely inject code directly into your session." },
                  { icon: Wand2, label: "Fast Prototyping", desc: "Go from a 'what if' to a playable feature instantly." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-2xl bg-white/[0.02] border border-white/5 group hover:border-sonas-accent/30 transition-all">
                    <item.icon className="h-6 w-6 text-sonas-accent shrink-0" />
                    <div className="space-y-1">
                      <p className="text-sm font-bold">{item.label}</p>
                      <p className="text-[11px] text-sonas-dim leading-snug">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-sonas-accent/10 blur-[80px] rounded-full" />
              <div className="relative aspect-square rounded-[48px] overflow-hidden border border-white/10 shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=2070" 
                  className="w-full h-full object-cover" 
                  alt="Studio Workspace"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-sonas-bg to-transparent opacity-60" />
                <div className="absolute bottom-8 left-8 right-8 p-6 liquid-glass rounded-3xl border border-white/10 space-y-3">
                   <div className="flex items-center gap-2">
                     <div className="h-2 w-2 rounded-full bg-sonas-accent animate-pulse" />
                     <span className="text-[10px] font-bold uppercase tracking-widest text-sonas-accent">Live Sync Stream</span>
                   </div>
                   <p className="text-xs font-mono text-sonas-muted leading-relaxed">
                     -- Injecting movement controller...<br/>
                     game:GetService("RunService").Heartbeat:Connect(function(dt)<br/>
                     &nbsp;&nbsp;-- Logic applied successfully.<br/>
                     end)
                   </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-56 px-6 max-w-7xl mx-auto text-center space-y-16">
          <div className="space-y-4">
            <h2 className="text-4xl lg:text-5xl font-syne font-bold">Pick your tools.</h2>
            <p className="text-sonas-muted">Choose the plan that matches your current development pace.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricing.map((plan, i) => (
              <div key={i} className={`p-10 rounded-[48px] border flex flex-col items-start text-left transition-all hover:scale-[1.02] ${plan.accent}`}>
                <div className="w-full flex justify-between items-start mb-8">
                  <h3 className="text-2xl font-syne font-bold">{plan.name}</h3>
                  <span className="text-4xl font-bold">{plan.price}</span>
                </div>
                <p className="text-sm text-sonas-muted mb-10">{plan.desc}</p>
                <div className="space-y-4 mb-12 flex-1">
                  {plan.features.map((feat, j) => (
                    <div key={j} className="flex items-center gap-3 text-xs font-medium">
                      <CheckCircle2 className="h-4 w-4 text-sonas-accent" />
                      {feat}
                    </div>
                  ))}
                </div>
                <Link to="/login" className="w-full">
                  <Button className={`w-full h-14 rounded-2xl font-bold transition-all ${plan.highlight ? 'bg-sonas-accent text-sonas-bg' : 'bg-white/5 border border-white/10 hover:bg-white/10'}`}>
                    {plan.button}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-16 px-6 lg:px-12 bg-sonas-bg/80 backdrop-blur-xl relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-12">
          <img src={logo} alt="Sonas" className="h-6 w-auto opacity-30 grayscale" />
          
          <div className="flex flex-col items-center gap-6">
            <div className="flex gap-8 text-[10px] font-bold uppercase tracking-[0.2em] text-sonas-dim">
              <Link to="/terms" className="hover:text-sonas-accent transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-sonas-accent transition-colors">Privacy</Link>
              <Link to="/aup" className="hover:text-sonas-accent transition-colors">Acceptable Use</Link>
            </div>
            <a 
              href="https://discord.gg/S25DbuEVSy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-2 rounded-full bg-[#5865F2]/10 border border-[#5865F2]/20 text-[#5865F2] text-[10px] font-bold uppercase tracking-widest hover:bg-[#5865F2]/20 transition-all"
            >
              <MessageCircle size={14} /> Join the Community
            </a>
          </div>

          <p className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim/40">© 2024 Sonas Workspace</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;